import { z } from 'zod';

export const REMOTE_APP_PROTOCOL_VERSION = 1;

const RequestIdSchema = z.string().min(1).max(128);
const IdSchema = z.string().min(1).max(128);
const NameSchema = z.string().min(1).max(256);
const PathSchema = z.string().max(4096);
const PromptSchema = z.string().max(64 * 1024);
const WorkspaceAttentionSchema = z
  .object({
    kind: z.enum(['input-required', 'turn-complete', 'error', 'idle', 'generic']),
    source: z.string().min(1).max(128),
    updatedAt: z.number().int().nonnegative(),
    message: z.string().max(4096).optional(),
    terminalId: IdSchema.optional(),
  })
  .strict();

export const RemoteWorkspaceSummarySchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    workingDirectory: PathSchema,
    projectId: IdSchema.nullable().optional(),
    projectName: NameSchema.optional(),
    projectColor: z.string().max(128).optional(),
    branchName: z.string().max(256).optional(),
    status: z.enum(['active', 'stashed', 'archived']),
    provisioning: z.boolean(),
    agentActive: z.boolean().optional(),
    needsAttention: z.boolean().optional(),
    attention: WorkspaceAttentionSchema.optional(),
    createdAt: z.number().int().nonnegative().optional(),
    lastActivityAt: z.number().int().nonnegative().optional(),
    git: z
      .object({
        additions: z.number().int().nonnegative(),
        deletions: z.number().int().nonnegative(),
        aheadTargetBranch: z.number().int().nonnegative(),
        behindTargetBranch: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    primaryTerminalId: IdSchema.optional(),
    terminals: z.array(
      z.object({
        id: IdSchema,
        name: NameSchema,
        status: z.enum(['starting', 'running', 'stopped']),
        agentActive: z.boolean().optional(),
        attention: WorkspaceAttentionSchema.optional(),
      })
    ),
  })
  .strict();

export const RemoteProjectSummarySchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    repositoryPath: PathSchema,
    color: z.string().max(128).optional(),
  })
  .strict();

export const RemoteLaunchModeSummarySchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    color: z.string().max(128).optional(),
  })
  .strict();

export const RemoteAgentPresetSummarySchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    launchModes: z.array(RemoteLaunchModeSummarySchema),
    lastLaunchModeId: IdSchema.nullable().optional(),
  })
  .strict();

export const RemoteTerminalSnapshotSchema = z
  .object({
    workspaceId: IdSchema,
    terminalId: IdSchema,
    status: z.enum(['starting', 'running', 'stopped']),
    buffer: z.string(),
    seq: z.number().int().min(0),
  })
  .strict();

export const RemoteListWorkspacesSchema = z
  .object({
    type: z.literal('app:workspaces:list'),
    requestId: RequestIdSchema,
  })
  .strict();

export const RemoteLaunchOptionsGetSchema = z
  .object({
    type: z.literal('app:launch-options:get'),
    requestId: RequestIdSchema,
  })
  .strict();

export const RemoteLaunchOptionsSchema = z
  .object({
    type: z.literal('app:launch-options'),
    requestId: RequestIdSchema,
    projects: z.array(RemoteProjectSummarySchema),
    presets: z.array(RemoteAgentPresetSummarySchema),
    defaultLaunchModeId: IdSchema.optional(),
  })
  .strict();

export const RemoteWorkspacesSchema = z
  .object({
    type: z.literal('app:workspaces'),
    requestId: RequestIdSchema,
    workspaces: z.array(RemoteWorkspaceSummarySchema),
  })
  .strict();

export const RemoteCreateWorkspaceSchema = z
  .object({
    type: z.literal('app:workspace:create'),
    requestId: RequestIdSchema,
    projectId: IdSchema.optional(),
    name: NameSchema.optional(),
    branchName: z.string().min(1).max(256).optional(),
    presetId: IdSchema.optional(),
    launchModeId: IdSchema.optional(),
    initialPrompt: PromptSchema.optional(),
  })
  .strict();

export const RemoteWorkspaceCreatedSchema = z
  .object({
    type: z.literal('app:workspace:created'),
    requestId: RequestIdSchema,
    workspace: RemoteWorkspaceSummarySchema,
  })
  .strict();

export const RemoteStartWorkspaceSchema = z
  .object({
    type: z.literal('app:workspace:start'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
  })
  .strict();

export const RemoteWorkspaceStartedSchema = z
  .object({
    type: z.literal('app:workspace:started'),
    requestId: RequestIdSchema,
    workspace: RemoteWorkspaceSummarySchema,
  })
  .strict();

export const RemoteStopWorkspaceSchema = z
  .object({
    type: z.literal('app:workspace:stop'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
  })
  .strict();

export const RemoteWorkspaceStoppedSchema = z
  .object({
    type: z.literal('app:workspace:stopped'),
    requestId: RequestIdSchema,
    workspace: RemoteWorkspaceSummarySchema,
  })
  .strict();

export const RemoteDeleteWorkspaceSchema = z
  .object({
    type: z.literal('app:workspace:delete'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
  })
  .strict();

export const RemoteWorkspaceDeletedSchema = z
  .object({
    type: z.literal('app:workspace:deleted'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
  })
  .strict();

export const RemoteRegenerateWorkspaceTitleSchema = z
  .object({
    type: z.literal('app:workspace:title:regenerate'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
  })
  .strict();

export const RemoteWorkspaceTitleRegeneratedSchema = z
  .object({
    type: z.literal('app:workspace:title:regenerated'),
    requestId: RequestIdSchema,
    workspace: RemoteWorkspaceSummarySchema,
  })
  .strict();

export const RemoteAttachTerminalSchema = z
  .object({
    type: z.literal('app:terminal:attach'),
    requestId: RequestIdSchema,
    workspaceId: IdSchema,
    terminalId: IdSchema.optional(),
    cols: z.number().int().min(1).max(1000).optional(),
    rows: z.number().int().min(1).max(1000).optional(),
  })
  .strict();

export const RemoteTerminalAttachedSchema = z
  .object({
    type: z.literal('app:terminal:attached'),
    requestId: RequestIdSchema,
    snapshot: RemoteTerminalSnapshotSchema,
  })
  .strict();

export const RemoteTerminalDataSchema = z
  .object({
    type: z.literal('app:terminal:data'),
    workspaceId: IdSchema,
    terminalId: IdSchema,
    data: z.string(),
    seq: z.number().int().min(0),
  })
  .strict();

export const RemoteTerminalStatusSchema = z
  .object({
    type: z.literal('app:terminal:status'),
    workspaceId: IdSchema.optional(),
    terminalId: IdSchema,
    status: z.enum(['starting', 'running', 'stopped']),
  })
  .strict();

export const RemoteTerminalInputSchema = z
  .object({
    type: z.literal('app:terminal:input'),
    terminalId: IdSchema,
    data: z.string(),
  })
  .strict();

export const RemoteTerminalResizeSchema = z
  .object({
    type: z.literal('app:terminal:resize'),
    terminalId: IdSchema,
    cols: z.number().int().min(1).max(1000),
    rows: z.number().int().min(1).max(1000),
  })
  .strict();

// Voice dictation. The client records mic audio, downsamples to 16 kHz mono,
// and ships it as little-endian Int16 PCM base64. The desktop transcribes
// locally with the same on-device model the renderer uses and writes the
// resulting text straight into the attached terminal's PTY.
export const RemoteVoiceStatusSchema = z
  .object({
    type: z.literal('app:voice:status'),
    requestId: RequestIdSchema,
  })
  .strict();

export const RemoteVoiceAvailabilitySchema = z
  .object({
    type: z.literal('app:voice:availability'),
    requestId: RequestIdSchema,
    available: z.boolean(),
  })
  .strict();

export const RemoteVoiceTranscribeSchema = z
  .object({
    type: z.literal('app:voice:transcribe'),
    requestId: RequestIdSchema,
    terminalId: IdSchema.optional(),
    purpose: z.enum(['terminal', 'prompt']).optional(),
    // Base64 (standard alphabet) of little-endian Int16 PCM, 16 kHz mono.
    // A single recording is split into ordered chunks so each frame stays
    // under the wire-level MAX_FRAME_CHARS cap (64 KiB) once the app frame is
    // encrypted and base64-wrapped into an `e2e:enc` envelope; the desktop
    // reassembles them by requestId. `seq` is the 0-based chunk index and
    // `final` marks the last chunk, which triggers transcription. The client
    // chunks at 32 KiB of PCM (~43.7 KiB of base64); cap the field a little
    // above that so an oversized chunk fails as a clean app-level error here
    // rather than as an opaque transport teardown.
    audio: z
      .string()
      .min(4)
      .max(48 * 1024),
    seq: z.number().int().min(0).max(4096),
    final: z.boolean(),
    // When true, the desktop appends a carriage return after typing the
    // transcript so the terminal submits it. Read from the final chunk.
    enter: z.boolean(),
  })
  .strict();

export const RemoteVoiceTranscribedSchema = z
  .object({
    type: z.literal('app:voice:transcribed'),
    requestId: RequestIdSchema,
    text: z.string().max(64 * 1024),
  })
  .strict();

export const RemoteErrorSchema = z
  .object({
    type: z.literal('app:error'),
    requestId: RequestIdSchema.optional(),
    code: z.string().min(1).max(128),
    message: z.string().min(1).max(2048),
  })
  .strict();

export const RemoteAppFrameSchema = z.discriminatedUnion('type', [
  RemoteListWorkspacesSchema,
  RemoteLaunchOptionsGetSchema,
  RemoteLaunchOptionsSchema,
  RemoteWorkspacesSchema,
  RemoteCreateWorkspaceSchema,
  RemoteWorkspaceCreatedSchema,
  RemoteStartWorkspaceSchema,
  RemoteWorkspaceStartedSchema,
  RemoteStopWorkspaceSchema,
  RemoteWorkspaceStoppedSchema,
  RemoteDeleteWorkspaceSchema,
  RemoteWorkspaceDeletedSchema,
  RemoteRegenerateWorkspaceTitleSchema,
  RemoteWorkspaceTitleRegeneratedSchema,
  RemoteAttachTerminalSchema,
  RemoteTerminalAttachedSchema,
  RemoteTerminalDataSchema,
  RemoteTerminalStatusSchema,
  RemoteTerminalInputSchema,
  RemoteTerminalResizeSchema,
  RemoteVoiceStatusSchema,
  RemoteVoiceAvailabilitySchema,
  RemoteVoiceTranscribeSchema,
  RemoteVoiceTranscribedSchema,
  RemoteErrorSchema,
]);

export type RemoteWorkspaceSummary = z.infer<typeof RemoteWorkspaceSummarySchema>;
export type RemoteProjectSummary = z.infer<typeof RemoteProjectSummarySchema>;
export type RemoteLaunchModeSummary = z.infer<typeof RemoteLaunchModeSummarySchema>;
export type RemoteAgentPresetSummary = z.infer<typeof RemoteAgentPresetSummarySchema>;
export type RemoteTerminalSnapshot = z.infer<typeof RemoteTerminalSnapshotSchema>;
export type RemoteListWorkspaces = z.infer<typeof RemoteListWorkspacesSchema>;
export type RemoteLaunchOptionsGet = z.infer<typeof RemoteLaunchOptionsGetSchema>;
export type RemoteLaunchOptions = z.infer<typeof RemoteLaunchOptionsSchema>;
export type RemoteWorkspaces = z.infer<typeof RemoteWorkspacesSchema>;
export type RemoteCreateWorkspace = z.infer<typeof RemoteCreateWorkspaceSchema>;
export type RemoteWorkspaceCreated = z.infer<typeof RemoteWorkspaceCreatedSchema>;
export type RemoteStartWorkspace = z.infer<typeof RemoteStartWorkspaceSchema>;
export type RemoteWorkspaceStarted = z.infer<typeof RemoteWorkspaceStartedSchema>;
export type RemoteStopWorkspace = z.infer<typeof RemoteStopWorkspaceSchema>;
export type RemoteWorkspaceStopped = z.infer<typeof RemoteWorkspaceStoppedSchema>;
export type RemoteDeleteWorkspace = z.infer<typeof RemoteDeleteWorkspaceSchema>;
export type RemoteWorkspaceDeleted = z.infer<typeof RemoteWorkspaceDeletedSchema>;
export type RemoteRegenerateWorkspaceTitle = z.infer<
  typeof RemoteRegenerateWorkspaceTitleSchema
>;
export type RemoteWorkspaceTitleRegenerated = z.infer<
  typeof RemoteWorkspaceTitleRegeneratedSchema
>;
export type RemoteAttachTerminal = z.infer<typeof RemoteAttachTerminalSchema>;
export type RemoteTerminalAttached = z.infer<typeof RemoteTerminalAttachedSchema>;
export type RemoteTerminalData = z.infer<typeof RemoteTerminalDataSchema>;
export type RemoteTerminalStatus = z.infer<typeof RemoteTerminalStatusSchema>;
export type RemoteTerminalInput = z.infer<typeof RemoteTerminalInputSchema>;
export type RemoteTerminalResize = z.infer<typeof RemoteTerminalResizeSchema>;
export type RemoteVoiceStatus = z.infer<typeof RemoteVoiceStatusSchema>;
export type RemoteVoiceAvailability = z.infer<typeof RemoteVoiceAvailabilitySchema>;
export type RemoteVoiceTranscribe = z.infer<typeof RemoteVoiceTranscribeSchema>;
export type RemoteVoiceTranscribed = z.infer<typeof RemoteVoiceTranscribedSchema>;
export type RemoteError = z.infer<typeof RemoteErrorSchema>;
export type RemoteAppFrame = z.infer<typeof RemoteAppFrameSchema>;

export function parseRemoteAppFrame(value: unknown): RemoteAppFrame | null {
  const parsed = RemoteAppFrameSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
